import { MoreDotsIcon } from '@tableau/icons/MoreDotsIcon';
import { UsersGroupIcon } from '@tableau/icons/UsersGroupIcon';
import { DropdownButton, DropdownButtonType, List, ListItem, Tooltip } from '@tableau/widgets';
import { FormikContextType, useField } from 'formik';
import React, { ReactElement, useRef, useState } from 'react';
import { classes } from 'typestyle';

import { Messages } from '~src/codegen/Localize';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { ISiteTrial, TrialStatus } from '~src/siteManagement/serverApi/types/ISiteTrial';
import { IGroupBasicInfo } from '~src/userManagement/groups/models/IGroupInfo';

import { AgentBetaWarningDialog } from './AgentBetaWarningDialog';
import { AgentGroupPermissionsDialog } from './AgentGroupPermissionsDialog';
import { AgentRowStatusMessage } from './AgentRowStatusMessage';
import {styles } from './AgentSectionStyles';
import { _groupName, agentSupportsGroups, getAgentAvailabilityInfo, getAgentFieldPath, getAgentLabel, getTestId } from './AgentSectionsUtils';
import { Agent, AgentPerm, AgentPermissionsSectionProps } from './AgentSectionTypes';

interface AgentRowProps {
    agent: Agent;
    allowTestRegistration: boolean;
    allUsersGroup: IGroupBasicInfo;
    formik: FormikContextType<AgentPermissionsSectionProps>;
    groups: IGroupBasicInfo[];
    isTrialSite: boolean;
    isTryItMode: boolean;
    registered: boolean;
    tableauAITrial?: ISiteTrial;
    // On-prem only: false while the LLM connection / model tests have not validated. Undefined on
    // Cloud (no LLM connection gating). See AgentPermissionsTable's prop of the same name.
    connectionValid?: boolean;
    // On-prem only: intercept "turn on" so the parent can require model selection first — if no
    // models are selected, open the Select Models dialog and enable the feature only on Apply.
    // Return true if the parent handled the enable (the row must NOT also flip the setting on);
    // return false to fall through to the normal enable. Undefined on Cloud, where enabling is
    // always immediate. See AgentPermissionsTable.
    onRequestEnable?: (agent: Agent) => boolean;
    // On-prem only: returns true when this agent's provider/license/auth combination isn't
    // supported, so its enable action must be blocked (an already-on feature can still be turned
    // off). Undefined on Cloud. See AgentPermissionsTable.
    isEnableForceDisabled?: (agent: Agent) => boolean;
}

interface AgentActionMenuProps {
    agent: Agent;
    agentLabel: string;
    closeDropdown: () => void;
    toggleGroupDialog: (state?: boolean) => void;
    setAgentPermission: {
       enabled: (e: boolean) => void;
       groupId: (g: string) => void;
    };
    allUsersGroup: IGroupBasicInfo;
    openBetaDialog: () => void;
}

export const ActionMenuOptions = (agent: Agent): ListItem[] => {
    const options: ListItem[] =  [
    { value: agent.enabled ? 'deactivate' : 'activate',
      content: agent.enabled ? Messages.settings_agents_state_deactivate() : Messages.settings_agents_state_activate(),
      testId: getTestId('ActionMenuOption_toggle', agent.id)
    }];
    if (agentSupportsGroups(agent)) options.push({
            value: 'grantAccess',
            content: Messages.settings_agents_grant_access_to(),
            disabled: !agent.enabled,
            testId: getTestId('ActionMenuOption_grantAccess', agent.id)});
    return options;
};


export const AgentActionsMenu = ({agent, agentLabel, closeDropdown, setAgentPermission, toggleGroupDialog, allUsersGroup, openBetaDialog}: AgentActionMenuProps) => {
    const ref = useRef<List>(null);
    const handleClick = (item: string): void => {
        switch (item) {
            case 'activate': {
                if (!agent.betaFlag || !FeatureFlags[agent.betaFlag]) {
                    setAgentPermission.enabled(true);
                    break;
                }
                openBetaDialog();
            }
            case 'deactivate': {
                setAgentPermission.enabled(false);
                break;
            }
            case 'grantAccess': {
                toggleGroupDialog(true);
                break;
            }
        }
        ref.current.setActiveItemIndex(-1);
        closeDropdown();
    };
    return <List
        ref={ref} role="menu"
        items={ActionMenuOptions(agent)}
        id={`Action Menu for ${agentLabel}`}
        onSelect={handleClick}
        selectedValue=""
        testId={getTestId('AgentActionsMenu', agent.id)}
    />;
};

interface AgentActionButtonProps  {
    agent: Agent;
    agentLabel: string;
    toggleGroupDialog: (state?: boolean) => void;
    disabled: boolean;
    setAgentPermission: {
       enabled: (e: boolean) => void;
       groupId: (g: string) => void;
    };
    allUsersGroup: IGroupBasicInfo;
    openBetaDialog: () => void;
}

export const ActionButton = ({agent, agentLabel, toggleGroupDialog, disabled, setAgentPermission, allUsersGroup, openBetaDialog}: AgentActionButtonProps) => {
    const ref = useRef<DropdownButton>(null);
    const close = () => {
        ref.current?.toggle(false);
    };
    return <DropdownButton
                aria-controls={`Action Menu for ${agentLabel}`}
                label=""
                type={DropdownButtonType.Icon}
                popupContent={<AgentActionsMenu
                                    agent={agent}
                                    agentLabel={agentLabel}
                                    closeDropdown={close}
                                    toggleGroupDialog={toggleGroupDialog}
                                    setAgentPermission={setAgentPermission}
                                    allUsersGroup={allUsersGroup}
                                    openBetaDialog={openBetaDialog}
                                />
                            }
                ref={ref}
                focusOnClose={true}
                disabled={disabled}
                testId={getTestId('ActionButton', agent.id)}
                className={styles.actionButton}
            >
                <MoreDotsIcon size={14} />
            </DropdownButton>;
};

export const AgentRow = ({
    agent,
    allowTestRegistration,
    allUsersGroup,
    formik,
    groups,
    isTrialSite,
    isTryItMode,
    registered,
    tableauAITrial,
    connectionValid,
    onRequestEnable,
    isEnableForceDisabled
}: AgentRowProps): ReactElement => {
    const [groupDialogOpen, setGroupDialogOpen] = useState(false);
    const [betaDialogOpen, setBetaDialogOpen] = useState(false);
    const groupName = _groupName(groups);
    const agentLabel = getAgentLabel(agent, isTrialSite);
    const [enabled] = useField({name: getAgentFieldPath(agent, AgentPerm.Enabled), type: 'checkbox'});
    const [groupId] = useField({name: getAgentFieldPath(agent, AgentPerm.GroupId), type: 'text'});
    const { actionButtonDisabled: availabilityDisabled, isAvailable } = getAgentAvailabilityInfo({ agent, allowTestRegistration, isTrialSite, isTryItMode, registered, tableauAITrial });
    // On-prem: gate the Actions menu on the LLM connection/model tests. A feature that is OFF can
    // only be turned on once the connection is valid, but a feature that is already ON can always
    // be turned off so the admin is never stuck. connectionValid is undefined on Cloud, so this
    // adds no gating there.
    const blockedByConnection = connectionValid === false && !enabled.value;
    // On-prem: a feature whose provider/license/auth combination isn't supported can't be turned on;
    // an already-on feature can still be turned off, matching the connection gate above. The predicate
    // is undefined on Cloud, so this adds no gating there.
    const enableForceDisabled = isEnableForceDisabled ? isEnableForceDisabled(agent) : false;
    const blockedByForceDisable = enableForceDisabled && !enabled.value;
    const actionButtonDisabled = availabilityDisabled || blockedByConnection || blockedByForceDisable;
    const groupsNotAllowed = agent.dashboardQAFlag && FeatureFlags[agent.dashboardQAFlag];
    const isTryItModeWithNonActiveTrialAndAgentRequiringRegistration = isTryItMode && tableauAITrial?.status !== TrialStatus.ACTIVE && agent.requiresRegistration;
    const readyForGroups = agentSupportsGroups(agent)
                            && isAvailable
                            && enabled.value
                            && !isTryItModeWithNonActiveTrialAndAgentRequiringRegistration;

    const openBetaDialog = () => setBetaDialogOpen(true);

    if (agent.flag && !FeatureFlags[agent.flag]) return null;

    agent.groupId = groupId.value ?? null;
    agent.enabled = enabled.value;

    const setAgentPermission = {
        enabled: (enable: boolean) => {
            // On-prem: when turning a feature on, let the parent require model selection first
            // (opens the Select Models dialog and enables on Apply). If it handled the enable, don't
            // also flip the setting here — otherwise the feature would turn on with no models behind
            // it. onRequestEnable is undefined on Cloud, so enabling stays immediate there.
            if (enable && onRequestEnable && onRequestEnable(agent)) {
                return;
            }
            formik.setFieldValue(getAgentFieldPath(agent, AgentPerm.Enabled), enable);
        },
        groupId: (gid: string) => formik.setFieldValue(getAgentFieldPath(agent, AgentPerm.GroupId), gid),
    };

    const toggleGroupDialog = (state?: boolean) => {
        setGroupDialogOpen(state ?? !groupDialogOpen);
    };

    const toggleBetaDialog = (state?: boolean) => {
        setBetaDialogOpen(state ?? !betaDialogOpen);
    };

    let groupText: React.ReactChild;
    const canShowGroup = groupId.value && (!agent.readyForGroupsFlag || FeatureFlags[agent.readyForGroupsFlag]);
    if (!enabled.value || !isAvailable || isTryItModeWithNonActiveTrialAndAgentRequiringRegistration) {
        groupText = Messages.settings_agents_access_group_none();
    } else if (groupsNotAllowed || !canShowGroup) {
        groupText = Messages.settings_agents_access_group_all_users();
    } else {
        groupText = <span>{groupName(groupId.value)}</span>;
    }

    const showGroupIcon = readyForGroups && groupId.value && groupId.value !== allUsersGroup?.groupLuid;

    return(
        <div data-tb-test-id={getTestId('AgentRow', agent.id)} className={classes(styles.row, styles.bodyRow)}>
            <div className={classes(styles.agentNameCell, styles.cell)}>
                <Tooltip  afterDelay={true} tooltipContent={agentLabel} followCursor={true} testId={getTestId('StatusMessage_Tooltip', agent.id)}>
                    <span className={styles.nameSpan}>{agentLabel}</span>
                </Tooltip>
            </div>
            <div className={classes(styles.cell, styles.status)}>
                <AgentRowStatusMessage
                    agent={agent}
                    isAvailable={isAvailable}
                    isTryItMode={isTryItMode}
                    tableauAITrial={tableauAITrial}
                />
            </div>
            <div className={classes(styles.cell, styles.group)} data-tb-test-id={getTestId('AgentRow_access_group', agent.id)}>
                <Tooltip
                    afterDelay={true} tooltipContent={groupText}
                    followCursor={true}
                    testId={getTestId('GroupMessage_Tooltip', agent.id)}>
                        <span className={styles.hasIcon}>{ showGroupIcon && <UsersGroupIcon
                                size={14}
                                className={styles.icon}/>}{groupText}
                        </span>
                </Tooltip>
            </div>
            <div className={classes(styles.cell, styles.actions)}>
                <ActionButton
                    agent={agent}
                    agentLabel={agentLabel}
                    toggleGroupDialog={toggleGroupDialog}
                    disabled={actionButtonDisabled}
                    setAgentPermission={setAgentPermission}
                    allUsersGroup={allUsersGroup}
                    openBetaDialog={openBetaDialog}
                />
                {groupDialogOpen && <AgentGroupPermissionsDialog
                    agent={agent}
                    agentLabel={agentLabel}
                    groups={groups}
                    handleClose={() => toggleGroupDialog(false)}
                    chooseGroup={setAgentPermission.groupId}
                    />}
                {betaDialogOpen && <AgentBetaWarningDialog
                    agent={agent}
                    groups={groups}
                    handleClose={() => toggleBetaDialog(false)}
                    activate={() => setAgentPermission.enabled(true)}
                    />}
            </div>
        </div>);
};
